import { TASK_STATUS_COLORS, TASK_STATUS_BG_COLORS, TASK_STATUS_LABELS, PRIORITY_COLORS, PRIORITY_BG_COLORS, PRIORITY_LABELS, USER_ROLE_COLORS, USER_ROLE_LABELS, RATING_COLORS, RATING_LABELS, RATING_EMOJIS, AMENITY_TYPE_ICONS, VALID_STATUS_TRANSITIONS, } from './constants';
// ============================================
// DATE FORMATTING
// ============================================
/**
 * Format a date to a human-readable string
 * @param date - Date to format
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export function formatDate(date, options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
}) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', options);
}
/**
 * Format a date with time
 * @param date - Date to format
 * @returns Formatted date and time string
 */
export function formatDateTime(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
/**
 * Format a time only
 * @param date - Date to format
 * @returns Formatted time string
 */
export function formatTime(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
    });
}
/**
 * Format a date as relative time (e.g., "2 hours ago", "in 3 days")
 * @param date - Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    const diffWeek = Math.floor(diffDay / 7);
    const diffMonth = Math.floor(diffDay / 30);
    const isFuture = diffMs < 0;
    const absMin = Math.abs(diffMin);
    const absHour = Math.abs(diffHour);
    const absDay = Math.abs(diffDay);
    const absWeek = Math.abs(diffWeek);
    const absMonth = Math.abs(diffMonth);
    const formatUnit = (value, unit, future) => {
        const plural = value === 1 ? '' : 's';
        return future ? `in ${value} ${unit}${plural}` : `${value} ${unit}${plural} ago`;
    };
    if (Math.abs(diffSec) < 60) {
        return 'just now';
    }
    if (absMin < 60) {
        return formatUnit(absMin, 'minute', isFuture);
    }
    if (absHour < 24) {
        return formatUnit(absHour, 'hour', isFuture);
    }
    if (absDay < 7) {
        return formatUnit(absDay, 'day', isFuture);
    }
    if (absWeek < 4) {
        return formatUnit(absWeek, 'week', isFuture);
    }
    if (absMonth < 12) {
        return formatUnit(absMonth, 'month', isFuture);
    }
    // Fallback to formatted date for anything older
    return formatDate(d);
}
/**
 * Format a date as ISO string (YYYY-MM-DD)
 * @param date - Date to format
 * @returns ISO date string
 */
export function formatISODate(date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toISOString().split('T')[0];
}
// ============================================
// STATUS UTILITIES
// ============================================
/**
 * Get the color for a task status
 * @param status - Task status
 * @returns Hex color code
 */
export function getStatusColor(status) {
    return TASK_STATUS_COLORS[status];
}
/**
 * Get the background color for a task status
 * @param status - Task status
 * @returns Hex color code
 */
export function getStatusBgColor(status) {
    return TASK_STATUS_BG_COLORS[status];
}
/**
 * Get the label for a task status
 * @param status - Task status
 * @returns Human-readable label
 */
export function getStatusLabel(status) {
    return TASK_STATUS_LABELS[status];
}
/**
 * Check if a status transition is valid
 * @param from - Current status
 * @param to - Target status
 * @returns Whether the transition is allowed
 */
export function isValidStatusTransition(from, to) {
    return VALID_STATUS_TRANSITIONS[from].includes(to);
}
/**
 * Get valid next statuses for a given status
 * @param status - Current status
 * @returns Array of valid next statuses
 */
export function getValidNextStatuses(status) {
    return VALID_STATUS_TRANSITIONS[status];
}
// ============================================
// PRIORITY UTILITIES
// ============================================
/**
 * Get the color for a priority
 * @param priority - Priority value ('CRITICAL' or 'STANDARD')
 * @returns Hex color code
 */
export function getPriorityColor(priority) {
    return PRIORITY_COLORS[priority] ?? PRIORITY_COLORS.STANDARD;
}
/**
 * Get the background color for a priority
 * @param priority - Priority value
 * @returns Hex color code
 */
export function getPriorityBgColor(priority) {
    return PRIORITY_BG_COLORS[priority] ?? PRIORITY_BG_COLORS.STANDARD;
}
/**
 * Get the label for a priority
 * @param priority - Priority value
 * @returns Human-readable label
 */
export function getPriorityLabel(priority) {
    return PRIORITY_LABELS[priority] ?? 'Standard';
}
/**
 * Check if a priority is critical
 * @param priority - Priority value
 * @returns Whether the priority is critical
 */
export function isCriticalPriority(priority) {
    return priority === 'CRITICAL';
}
// ============================================
// RATING UTILITIES
// ============================================
/**
 * Get the color for a rating
 * @param rating - Rating value (1-5)
 * @returns Hex color code
 */
export function getRatingColor(rating) {
    const clamped = Math.max(1, Math.min(5, Math.round(rating)));
    return RATING_COLORS[clamped] ?? '#6b7280';
}
/**
 * Get the label for a rating
 * @param rating - Rating value (1-5)
 * @returns Human-readable label
 */
export function getRatingLabel(rating) {
    const clamped = Math.max(1, Math.min(5, Math.round(rating)));
    return RATING_LABELS[clamped] ?? 'Unknown';
}
/**
 * Get the emoji for a rating
 * @param rating - Rating value (1-5)
 * @returns Emoji character
 */
export function getRatingEmoji(rating) {
    const clamped = Math.max(1, Math.min(5, Math.round(rating)));
    return RATING_EMOJIS[clamped] ?? '?';
}
// ============================================
// USER ROLE UTILITIES
// ============================================
/**
 * Get the color for a user role
 * @param role - User role
 * @returns Hex color code
 */
export function getRoleColor(role) {
    return USER_ROLE_COLORS[role];
}
/**
 * Get the label for a user role
 * @param role - User role
 * @returns Human-readable label
 */
export function getRoleLabel(role) {
    return USER_ROLE_LABELS[role];
}
/**
 * Check if a role has admin privileges
 * @param role - UserRole or array of roles
 * @returns Whether any role is admin
 */
export function isAdmin(role) {
    if (Array.isArray(role))
        return role.includes('ADMIN');
    return role === 'ADMIN';
}
/**
 * Check if a role has manager privileges (admin or manager)
 * @param role - User role or array of roles
 * @returns Whether any role is manager or above
 */
export function isManagerOrAbove(role) {
    if (Array.isArray(role))
        return role.some(r => r === 'ADMIN' || r === 'MANAGER');
    return role === 'ADMIN' || role === 'MANAGER';
}
/**
 * Check if a user has a specific role
 */
export function hasRole(roles, role) {
    return roles.includes(role);
}
/**
 * Check if a user has any of the specified roles
 */
export function hasAnyRole(roles, check) {
    return roles.some(r => check.includes(r));
}
/**
 * Get the highest-privilege role from a roles array
 * Order: SUPER_ADMIN > ADMIN > MANAGER > TECHNICIAN
 */
export function getHighestRole(roles) {
    const order = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'TECHNICIAN'];
    for (const r of order) {
        if (roles.includes(r))
            return r;
    }
    return roles[0] || 'TECHNICIAN';
}
// ============================================
// ICON UTILITIES
// ============================================
/**
 * Get the icon for an amenity type
 * @param type - Amenity type name (lowercase)
 * @returns Emoji icon
 */
export function getAmenityTypeIcon(type) {
    const key = type.toLowerCase().replace(/\s+/g, '_');
    return AMENITY_TYPE_ICONS[key] ?? AMENITY_TYPE_ICONS.default ?? '?';
}
// ============================================
// STRING UTILITIES
// ============================================
/**
 * Truncate a string to a maximum length
 * @param str - String to truncate
 * @param maxLength - Maximum length
 * @param suffix - Suffix to add when truncated (default: '...')
 * @returns Truncated string
 */
export function truncate(str, maxLength, suffix = '...') {
    if (str.length <= maxLength)
        return str;
    return str.slice(0, maxLength - suffix.length) + suffix;
}
/**
 * Capitalize the first letter of a string
 * @param str - String to capitalize
 * @returns Capitalized string
 */
export function capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}
/**
 * Convert a string to title case
 * @param str - String to convert
 * @returns Title-cased string
 */
export function toTitleCase(str) {
    return str
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}
// ============================================
// NUMBER UTILITIES
// ============================================
/**
 * Format a number with commas
 * @param num - Number to format
 * @returns Formatted number string
 */
export function formatNumber(num) {
    return num.toLocaleString('en-US');
}
/**
 * Format a percentage
 * @param value - Decimal value (0-1) or percentage (0-100)
 * @param decimals - Number of decimal places
 * @param isDecimal - Whether input is decimal (0-1) or percentage (0-100)
 * @returns Formatted percentage string
 */
export function formatPercentage(value, decimals = 1, isDecimal = true) {
    const pct = isDecimal ? value * 100 : value;
    return `${pct.toFixed(decimals)}%`;
}
// ============================================
// QUERY STRING UTILITIES
// ============================================
/**
 * Build a query string from an object
 * @param params - Object with query parameters
 * @returns Query string (without leading ?)
 */
export function buildQueryString(params) {
    const searchParams = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.append(key, String(value));
        }
    }
    return searchParams.toString();
}
/**
 * Parse a query string to an object
 * @param queryString - Query string to parse
 * @returns Object with query parameters
 */
export function parseQueryString(queryString) {
    const params = new URLSearchParams(queryString);
    const result = {};
    for (const [key, value] of params.entries()) {
        result[key] = value;
    }
    return result;
}
export const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    requireUppercase: true,
    requireLowercase: true,
    requireNumber: true,
};
/**
 * Validate a password against requirements
 * @param password - Password to validate
 * @returns Validation result with errors
 */
export function validatePassword(password) {
    const errors = [];
    if (password.length < PASSWORD_REQUIREMENTS.minLength) {
        errors.push(`Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters`);
    }
    if (PASSWORD_REQUIREMENTS.requireUppercase && !/[A-Z]/.test(password)) {
        errors.push('Password must contain at least one uppercase letter');
    }
    if (PASSWORD_REQUIREMENTS.requireLowercase && !/[a-z]/.test(password)) {
        errors.push('Password must contain at least one lowercase letter');
    }
    if (PASSWORD_REQUIREMENTS.requireNumber && !/[0-9]/.test(password)) {
        errors.push('Password must contain at least one number');
    }
    return {
        valid: errors.length === 0,
        errors,
    };
}
/**
 * Get a formatted password requirements message
 * @returns Human-readable requirements string
 */
export function getPasswordRequirementsMessage() {
    return `Must be ${PASSWORD_REQUIREMENTS.minLength}+ characters with uppercase, lowercase, and number`;
}
/**
 * Validate email format
 * @param email - Email to validate
 * @returns Whether the email is valid
 */
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}
//# sourceMappingURL=utils.js.map